import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { helpCenterService } from '../../services/api';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const MinimiseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const TypingIndicator = () => (
    <div style={{ display: 'flex', gap: 4, padding: '8px 0' }}>
        {[0, 1, 2].map(i => (
            <div key={i} style={{
                width: 7, height: 7, borderRadius: '50%', background: C.green,
                animation: `typing 1.2s infinite ${i * 0.2}s`,
                opacity: 0.4,
            }} />
        ))}
        <style>{`
            @keyframes typing {
                0%, 60%, 100% { opacity: 0.4; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-4px); }
            }
        `}</style>
    </div>
);

const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null;
    let olStart = 1;

    const flushList = () => {
        if (listItems.length > 0) {
            if (listType === 'ol') {
                elements.push(
                    <ol key={`list-${elements.length}`} start={olStart} style={{ margin: '6px 0', paddingLeft: 24, fontSize: 13, lineHeight: 1.7, color: '#212529', listStyleType: 'decimal' }}>
                        {listItems.map((item, i) => <li key={i} style={{ marginBottom: 2 }}>{renderInline(item)}</li>)}
                    </ol>
                );
            } else {
                elements.push(
                    <ul key={`list-${elements.length}`} style={{ margin: '6px 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: '#212529', listStyleType: 'disc' }}>
                        {listItems.map((item, i) => <li key={i} style={{ marginBottom: 2 }}>{renderInline(item)}</li>)}
                    </ul>
                );
            }
            listItems = [];
            listType = null;
        }
    };

    const renderInline = (str) => {
        const parts = [];
        let remaining = str;
        let key = 0;
        const re = /\*\*(.+?)\*\*/g;
        let match;
        let lastIdx = 0;
        while ((match = re.exec(remaining)) !== null) {
            if (match.index > lastIdx) parts.push(<span key={key++}>{remaining.slice(lastIdx, match.index)}</span>);
            parts.push(<strong key={key++}>{match[1]}</strong>);
            lastIdx = re.lastIndex;
        }
        if (lastIdx < remaining.length) parts.push(<span key={key++}>{remaining.slice(lastIdx)}</span>);
        return parts.length > 0 ? parts : str;
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const olMatch = line.match(/^(\d+)\.\s+(.+)/);
        const ulMatch = line.match(/^[-*]\s+(.+)/);
        const headingMatch = line.match(/^#{1,3}\s+(.+)/);

        if (olMatch) {
            if (listType !== 'ol') {
                flushList();
                olStart = parseInt(olMatch[1], 10);
            }
            listType = 'ol';
            listItems.push(olMatch[2]);
        } else if (ulMatch) {
            if (listType !== 'ul') flushList();
            listType = 'ul';
            listItems.push(ulMatch[1]);
        } else {
            flushList();
            if (headingMatch) {
                elements.push(<div key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: 8, marginBottom: 4 }}>{renderInline(headingMatch[1])}</div>);
            } else if (line.trim() === '') {
                elements.push(<div key={i} style={{ height: 6 }} />);
            } else {
                elements.push(<div key={i} style={{ fontSize: 13, lineHeight: 1.6 }}>{renderInline(line)}</div>);
            }
        }
    }
    flushList();
    return elements;
};

const ChatPanel = ({ isOpen, isMinimised, onClose, onMinimise, onExpand }) => {
    const { t, language } = useLanguage();
    const isRTL = language === 'ar';
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [ticketMode, setTicketMode] = useState(null);
    const [ticketSummary, setTicketSummary] = useState('');
    const [ticketSubmitted, setTicketSubmitted] = useState(false);
    const [chatFeedback, setChatFeedback] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && !isMinimised) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [messages, isOpen, isMinimised]);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([{
                role: 'assistant',
                content: t('help_chat_welcome'),
                timestamp: new Date(),
            }]);
        }
    }, [isOpen, t]);

    const sendMessage = async () => {
        if (!input.trim() || loading) return;
        const userMsg = { role: 'user', content: input.trim(), timestamp: new Date() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const history = messages
                .filter(m => m.role === 'user' || m.role === 'assistant')
                .map(m => ({ role: m.role, content: m.content }));

            const response = await helpCenterService.chat(userMsg.content, history);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.response,
                timestamp: new Date(),
            }]);
        } catch {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: t('help_chat_error'),
                timestamp: new Date(),
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (ticketMode === 'confirm') {
                submitTicket();
            } else {
                sendMessage();
            }
        }
    };

    const startTicket = async () => {
        setTicketMode('generating');
        setLoading(true);
        try {
            const history = messages
                .filter(m => m.role === 'user' || m.role === 'assistant')
                .map(m => ({ role: m.role, content: m.content }));

            const response = await helpCenterService.chat(
                'Please summarise the issue I have been describing in 1-2 sentences. Write it as a support ticket summary — concise and factual. Just the summary, nothing else.',
                history,
            );
            setTicketSummary(response.response);
            setTicketMode('confirm');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: t('help_ticket_confirm_prompt') + '\n\n"' + response.response + '"',
                timestamp: new Date(),
            }]);
        } catch {
            setTicketMode(null);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: t('help_ticket_error'),
                timestamp: new Date(),
            }]);
        } finally {
            setLoading(false);
        }
    };

    const submitTicket = async () => {
        setLoading(true);
        try {
            const transcript = messages.map(m => ({ role: m.role, content: m.content }));
            await helpCenterService.createTicket(ticketSummary, transcript);
            setTicketMode(null);
            setTicketSubmitted(true);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: t('help_ticket_created'),
                timestamp: new Date(),
            }]);
        } catch {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: t('help_ticket_error'),
                timestamp: new Date(),
            }]);
        } finally {
            setLoading(false);
            setTicketMode(null);
        }
    };

    const resetChat = () => {
        setMessages([]);
        setTicketMode(null);
        setTicketSummary('');
        setTicketSubmitted(false);
        setChatFeedback(null);
    };

    const formatTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!isOpen) return null;

    // ─── Minimised Bar ──────────────────────────────────────────────────────
    if (isMinimised) {
        const lastMsg = messages[messages.length - 1];
        return (
            <div
                onClick={onExpand}
                style={{
                    position: 'fixed', bottom: 20, [isRTL ? 'left' : 'right']: 20, zIndex: 1000,
                    width: 320, padding: '12px 16px', background: C.white,
                    border: `1px solid ${C.border}`, borderRadius: 12, cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
            >
                <div style={{
                    width: 32, height: 32, borderRadius: '50%', background: C.greenLight,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: C.text }}>
                        {t('help_chat_title')}
                    </div>
                    {lastMsg && (
                        <div style={{
                            fontFamily: FONT, fontSize: 11, color: C.muted, whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {lastMsg.content.slice(0, 60)}
                        </div>
                    )}
                </div>
                <div onClick={e => { e.stopPropagation(); onClose(); }}
                    style={{ flexShrink: 0, color: C.mutedLight, cursor: 'pointer' }}>
                    <CloseIcon />
                </div>
            </div>
        );
    }

    // ─── Expanded Panel ─────────────────────────────────────────────────────
    return (
        <div style={{
            position: 'fixed', bottom: expanded ? 0 : 20, [isRTL ? 'left' : 'right']: expanded ? 0 : 20,
            zIndex: 1000, width: expanded ? 500 : 380, height: expanded ? '100vh' : 520,
            background: C.white, borderRadius: expanded ? 0 : 16, transition: 'all 0.2s ease',
            border: `1px solid ${C.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '14px 16px', background: C.green, color: C.white,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700 }}>{t('help_chat_title')}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <div onClick={resetChat} title={t('help_new_conversation')}
                        style={{ cursor: 'pointer', opacity: 0.7, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="1 4 1 10 7 10"/>
                            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                        </svg>
                    </div>
                    <div onClick={() => setExpanded(prev => !prev)} title={expanded ? 'Shrink' : 'Expand'}
                        style={{ cursor: 'pointer', opacity: 0.7, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {expanded ? <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></> : <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>}
                        </svg>
                    </div>
                    <div onClick={onMinimise} style={{ cursor: 'pointer', opacity: 0.7, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                        <MinimiseIcon />
                    </div>
                    <div onClick={onClose} style={{ cursor: 'pointer', opacity: 0.7, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                        <CloseIcon />
                    </div>
                </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {messages.map((msg, i) => (
                    <div key={i} style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: msg.role === 'user' ? (isRTL ? 'flex-start' : 'flex-end') : (isRTL ? 'flex-end' : 'flex-start'),
                    }}>
                        <div style={{
                            maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
                            background: msg.role === 'user' ? C.userBubble : C.white,
                            borderLeft: msg.role === 'assistant' && !isRTL ? `3px solid ${C.green}` : 'none',
                            borderRight: msg.role === 'assistant' && isRTL ? `3px solid ${C.green}` : 'none',
                            border: msg.role === 'user' ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <div style={{
                                fontFamily: FONT, fontSize: 13, color: C.text, lineHeight: 1.6,
                                wordBreak: 'break-word',
                            }}>
                                {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                            </div>
                        </div>
                        <span style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight, marginTop: 4, padding: '0 4px' }}>
                            {formatTime(msg.timestamp)}
                        </span>
                    </div>
                ))}
                {loading && (
                    <div style={{ alignSelf: isRTL ? 'flex-end' : 'flex-start' }}>
                        <TypingIndicator />
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Ticket prompt */}
            {ticketMode === 'confirm' && (
                <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
                    <button onClick={submitTicket} disabled={loading}
                        style={{
                            flex: 1, padding: '8px 12px', background: C.green, color: C.white,
                            border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            fontFamily: FONT, cursor: 'pointer',
                        }}>
                        {t('help_ticket_confirm')}
                    </button>
                    <button onClick={() => { setTicketMode(null); setTicketSummary(''); }}
                        style={{
                            padding: '8px 12px', background: C.bg, color: C.muted,
                            border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600,
                            fontFamily: FONT, cursor: 'pointer',
                        }}>
                        {t('help_ticket_cancel')}
                    </button>
                </div>
            )}

            {/* Ticket raise button OR feedback after submission */}
            {!ticketMode && !ticketSubmitted && messages.length >= 4 && (
                <div style={{ padding: '6px 16px', borderTop: `1px solid ${C.border}` }}>
                    <button onClick={startTicket} disabled={loading}
                        style={{
                            width: '100%', padding: '7px 12px', background: 'transparent',
                            border: `1px solid ${C.greenBorder}`, borderRadius: 6, fontSize: 12,
                            fontWeight: 600, fontFamily: FONT, color: C.green, cursor: 'pointer',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.target.style.background = C.greenLight}
                        onMouseLeave={e => e.target.style.background = 'transparent'}>
                        {t('help_raise_ticket')}
                    </button>
                </div>
            )}
            {ticketSubmitted && !chatFeedback && (
                <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('help_were_we_useful')}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {['yes', 'no'].map(val => (
                            <button key={val} onClick={() => setChatFeedback(val)}
                                style={{
                                    padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                    fontFamily: FONT, cursor: 'pointer', border: `1px solid ${C.border}`,
                                    background: C.white, color: C.muted, transition: 'all 0.15s',
                                }}>
                                {val === 'yes' ? t('help_yes') : t('help_no')}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {chatFeedback && (
                <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.green, fontWeight: 600 }}>{t('help_thanks_feedback')}</span>
                </div>
            )}

            {/* Input */}
            <div style={{
                padding: '12px 16px', borderTop: `1px solid ${C.border}`,
                display: 'flex', gap: 8, alignItems: 'flex-end',
            }}>
                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
                    onKeyDown={handleKeyDown}
                    placeholder={t('help_chat_placeholder')}
                    disabled={loading}
                    rows={1}
                    style={{
                        flex: 1, padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 8,
                        fontSize: 13, fontFamily: FONT, color: C.text, outline: 'none', background: C.bg,
                        direction: isRTL ? 'rtl' : 'ltr', resize: 'none', overflow: 'hidden',
                        minHeight: 38, maxHeight: 100, lineHeight: 1.4,
                    }}
                    onFocus={e => e.target.style.borderColor = C.green}
                    onBlur={e => e.target.style.borderColor = C.border}
                />
                <button
                    onClick={ticketMode === 'confirm' ? submitTicket : sendMessage}
                    disabled={!input.trim() || loading}
                    style={{
                        width: 38, height: 38, borderRadius: 8, border: 'none', background: C.green,
                        color: C.white, cursor: input.trim() && !loading ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: input.trim() && !loading ? 1 : 0.5, transition: 'opacity 0.15s',
                        flexShrink: 0,
                    }}
                >
                    <SendIcon />
                </button>
            </div>
        </div>
    );
};

export default ChatPanel;
